import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiTarget, FiArrowRight, FiArrowLeft, FiX, FiCheck, FiZap, FiEye, FiBarChart3, FiBookOpen, FiBell } = FiIcons;

const OnboardingTour = ({ onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const tourSteps = [
    {
      id: 'welcome',
      title: 'Welcome to DreamForge!',
      description: 'Your journey to habit mastery and vision achievement begins here. Let me show you around.',
      icon: FiTarget,
      gradient: 'from-forge-400 to-orange-500',
      position: 'center',
      action: 'Get Started'
    },
    {
      id: 'dashboard',
      title: 'Command Center',
      description: 'This is your daily dashboard where you track habits, see progress, and get motivated with daily affirmations.',
      icon: FiZap,
      gradient: 'from-emerald-400 to-green-500',
      target: '[data-tour="dashboard"]',
      position: 'bottom',
      action: 'Next'
    },
    {
      id: 'habits',
      title: 'Build Unstoppable Habits',
      description: 'Create, track, and master your daily habits. Set goals, track streaks, and build consistency.',
      icon: FiTarget,
      gradient: 'from-blue-400 to-indigo-500',
      target: '[data-tour="habits"]',
      position: 'bottom',
      action: 'Continue'
    },
    {
      id: 'vision',
      title: 'Vision Board Magic',
      description: 'Visualize your dreams and goals. Track progress, predict obstacles, and manifest your future.',
      icon: FiEye,
      gradient: 'from-purple-400 to-violet-500',
      target: '[data-tour="vision"]',
      position: 'bottom',
      action: 'Show Me'
    },
    {
      id: 'analytics',
      title: 'Track Your Dominance',
      description: 'Analyze patterns, identify your best days, and optimize your performance with detailed insights.',
      icon: FiBarChart3,
      gradient: 'from-pink-400 to-rose-500',
      target: '[data-tour="analytics"]',
      position: 'bottom',
      action: 'Explore'
    },
    {
      id: 'complete',
      title: 'Ready to Dominate!',
      description: 'You\'re all set! Start by creating your first habit or vision. Remember: small actions lead to big transformations.',
      icon: FiCheck,
      gradient: 'from-emerald-400 to-green-500',
      position: 'center',
      action: 'Start Forging'
    }
  ];

  const currentStepData = tourSteps[currentStep];

  const nextStep = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTour();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeTour = () => {
    setIsVisible(false);
    setTimeout(() => {
      localStorage.setItem('dreamforge_onboarding_completed', 'true');
      onComplete();
    }, 300);
  };

  const skipTour = () => {
    setIsVisible(false);
    setTimeout(() => {
      localStorage.setItem('dreamforge_onboarding_completed', 'true');
      onSkip();
    }, 300);
  };

  // Get target element position for positioning tooltip
  const getTargetPosition = (selector) => {
    if (!selector) return null;
    const element = document.querySelector(selector);
    if (!element) return null;
    
    const rect = element.getBoundingClientRect();
    return {
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
      height: rect.height
    };
  };

  const targetPosition = getTargetPosition(currentStepData.target);

  // Center modal for welcome and completion steps
  if (currentStepData.position === 'center') {
    return (
      <AnimatePresence>
        {isVisible && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-gradient-to-br from-shadow-900/95 to-shadow-800/95 backdrop-blur-sm rounded-3xl shadow-dominance max-w-lg w-full overflow-hidden border border-forge-400/30"
            >
              {/* Header */}
              <div className={`bg-gradient-to-r ${currentStepData.gradient} p-6 text-white relative overflow-hidden`}>
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-bl-full"></div>
                <div className="relative z-10 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                      <SafeIcon icon={currentStepData.icon} className="text-white text-xl" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">{currentStepData.title}</h2>
                      <p className="text-white/80 text-sm">
                        Step {currentStep + 1} of {tourSteps.length}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={skipTour}
                    className="p-2 hover:bg-white/10 rounded-xl transition-colors"
                  >
                    <SafeIcon icon={FiX} className="text-white" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-forge-200 text-lg mb-6 leading-relaxed">
                  {currentStepData.description}
                </p>

                {/* Progress */}
                <div className="mb-6">
                  <div className="flex justify-between text-sm text-forge-300 mb-2">
                    <span>Progress</span>
                    <span>{Math.round(((currentStep + 1) / tourSteps.length) * 100)}%</span>
                  </div>
                  <div className="w-full bg-shadow-800/50 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${((currentStep + 1) / tourSteps.length) * 100}%` }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      className={`h-2 rounded-full bg-gradient-to-r ${currentStepData.gradient}`}
                    />
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex justify-between">
                  <button
                    onClick={prevStep}
                    disabled={currentStep === 0}
                    className="px-4 py-2 text-forge-300 hover:text-forge-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiArrowLeft} />
                    <span>Previous</span>
                  </button>
                  
                  <div className="flex space-x-3">
                    {currentStep < tourSteps.length - 1 && (
                      <button
                        onClick={skipTour}
                        className="px-6 py-3 border border-forge-400/30 text-forge-300 rounded-xl hover:bg-shadow-700/50 transition-colors"
                      >
                        Skip Tour
                      </button>
                    )}
                    <button
                      onClick={nextStep}
                      className={`px-6 py-3 bg-gradient-to-r ${currentStepData.gradient} text-white rounded-xl hover:shadow-lg transition-all duration-300 flex items-center space-x-2`}
                    >
                      <span>{currentStepData.action}</span>
                      <SafeIcon icon={currentStep === tourSteps.length - 1 ? FiCheck : FiArrowRight} />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    );
  }

  // Tooltip for targeted elements
  return (
    <AnimatePresence>
      {isVisible && targetPosition && (
        <>
          {/* Overlay */}
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" />
          
          {/* Highlight */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed z-40 border-2 border-forge-400 rounded-xl shadow-power"
            style={{
              top: targetPosition.top - 4,
              left: targetPosition.left - 4,
              width: targetPosition.width + 8,
              height: targetPosition.height + 8,
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)'
            }}
          />

          {/* Tooltip */}
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className="fixed z-50 bg-gradient-to-br from-shadow-900/95 to-shadow-800/95 backdrop-blur-sm rounded-2xl shadow-dominance border border-forge-400/30 max-w-sm"
            style={{
              top: targetPosition.top + targetPosition.height + 16,
              left: Math.max(16, Math.min(targetPosition.left, window.innerWidth - 384 - 16))
            }}
          >
            {/* Arrow */}
            <div 
              className="absolute w-3 h-3 bg-gradient-to-br from-shadow-900/95 to-shadow-800/95 border-l border-t border-forge-400/30 transform rotate-45"
              style={{ top: -6, left: Math.max(24, targetPosition.left + targetPosition.width/2 - Math.max(16, Math.min(targetPosition.left, window.innerWidth - 384 - 16))) }}
            />
            
            <div className="p-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className={`w-10 h-10 bg-gradient-to-r ${currentStepData.gradient} rounded-xl flex items-center justify-center`}>
                  <SafeIcon icon={currentStepData.icon} className="text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-forge-400">{currentStepData.title}</h3>
                  <p className="text-xs text-forge-300">
                    Step {currentStep + 1} of {tourSteps.length}
                  </p>
                </div>
              </div>
              
              <p className="text-forge-200 text-sm mb-4">
                {currentStepData.description}
              </p>
              
              <div className="flex justify-between items-center">
                <button
                  onClick={skipTour}
                  className="text-forge-400 hover:text-forge-200 text-sm transition-colors"
                >
                  Skip tour
                </button>
                
                <div className="flex space-x-2">
                  <button
                    onClick={prevStep}
                    disabled={currentStep === 0}
                    className="p-2 text-forge-400 hover:text-forge-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <SafeIcon icon={FiArrowLeft} />
                  </button>
                  <button
                    onClick={nextStep}
                    className={`px-4 py-2 bg-gradient-to-r ${currentStepData.gradient} text-white rounded-lg hover:shadow-lg transition-all duration-300 flex items-center space-x-2 text-sm`}
                  >
                    <span>{currentStepData.action}</span>
                    <SafeIcon icon={FiArrowRight} />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default OnboardingTour;